import { useState } from "react";
import { ArrowLeft, ShieldAlert, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { AskReviewButton } from "./AskReviewButton";
import { PaidActionDialog } from "./PaidActionDialog";
import api, { errorText, frontImage } from "../lib/api";
import { holdCost, isWaiting, openBlocks, usd } from "../lib/viewHold";

// Step 2 for an "I have a render" upload the view check holds: why, and the two ways forward.
export const UploadHoldCard = ({ creation, onUpdate, onBack }) => {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const reason = openBlocks(creation.view_check).front?.reason;
  const cost = holdCost(creation.view_check, "ai_render");

  const aiRender = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/creations/${creation.id}/ai-render`);
      setAsking(false);
      onUpdate(data);
      toast.success("AI render made from your image. Approve it or regenerate.");
    } catch (e) {
      toast.error(errorText(e, "Could not make the AI render. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="upload-hold-card" className="animate-pop grid md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-6 items-start">
      <figure className="rounded-3xl border-2 border-destructive/40 bg-card overflow-hidden">
        <img data-testid="upload-hold-image" src={frontImage(creation)} alt="Your uploaded image" className="w-full aspect-square object-contain bg-muted/40" />
        <figcaption className="px-4 py-2 text-xs font-mono2 uppercase tracking-widest text-muted-foreground">Your upload</figcaption>
      </figure>
      <div className="space-y-5">
        <div data-testid="upload-hold-reason" className="rounded-3xl border border-destructive/30 bg-destructive/5 p-5">
          <p className="font-heading text-xl font-bold flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-destructive shrink-0" /> We can't sculpt this image yet
          </p>
          <p className="mt-2 text-sm leading-relaxed">{reason}</p>
        </div>
        <div className="rounded-3xl border border-border bg-card/60 p-5 space-y-3">
          <p className="font-semibold">Generate an AI render from it instead</p>
          <p className="text-sm text-muted-foreground">
            We use your image as the photo and draw one AI duck render from it, the same as a photo upload. You approve that render before anything is sculpted.
          </p>
          <Button data-testid="upload-hold-ai-render-btn" onClick={() => setAsking(true)} className="rounded-full h-11 gap-2 font-bold">
            <Sparkles className="h-4 w-4" /> Generate an AI render · about {usd(cost)}
          </Button>
        </div>
        <div className="rounded-3xl border border-border bg-card/60 p-5 space-y-3">
          <p className="font-semibold">{creation.view_check?.broken?.front?.code === "not_render" ? "Think it is a proper 3D-style render?" : "Think it looks right?"}</p>
          <AskReviewButton creation={creation} onUpdate={onUpdate} />
        </div>
        <Button data-testid="upload-hold-back-btn" variant="ghost" onClick={onBack} className="rounded-full gap-2">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
      </div>
      <PaidActionDialog
        open={asking}
        title="Generate an AI render from your image?"
        what="One AI front render is made on the normal photo path. Your upload stays on the duck as its photo."
        cost={cost}
        cancelsReview={isWaiting(creation)}
        confirmLabel="Generate the render"
        busy={busy}
        onCancel={() => setAsking(false)}
        onConfirm={aiRender}
      />
    </div>
  );
};
